// components/common/SearchField.jsx
//
// The one search box of the app, on a page or in a popup: label, magnifier,
// and an X that empties the box. A plain search uses it directly;
// TypeaheadSearchField wraps it with a suggestion popper. One input, so every
// search box looks and clears the same way — searchFields.test.js fails on any
// other text input whose props mention search.
//
// `onChange` receives the text, not the event. The X calls `onChange('')`
// unless the caller passes `onClear` — a typeahead's X must also drop a
// committed filter the draft no longer shows.
//
// `disabled` locks the X with the box. Any other prop (className, onKeyDown,
// onFocus, autoFocus, sx) goes to the MUI TextField; `htmlInputProps` goes to
// the <input> itself.

import React from 'react';
import { TextField, IconButton, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICON, MagnifyingGlass, X } from '../../icons';

export default function SearchField({
  label,
  placeholder,
  value,
  onChange,
  showClear = value !== '',
  onClear = () => onChange(''),
  clearLabel,
  loading = false,
  disabled = false,
  htmlInputProps,
  ...textFieldProps
}) {
  const { t } = useTranslation();
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      {...textFieldProps}
      slotProps={{
        htmlInput: htmlInputProps,
        input: {
          startAdornment: <MagnifyingGlass size={ICON.xl} style={{ marginRight: 8, flexShrink: 0 }} />,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {showClear && (
                <IconButton
                  size="small"
                  aria-label={clearLabel ?? t('searchFilters.clear')}
                  onClick={onClear}
                  disabled={disabled}
                  edge="end"
                >
                  <X size={ICON.md} />
                </IconButton>
              )}
            </>
          ),
        },
      }}
    />
  );
}
